// @flow

import { put, call, all, takeEvery } from "redux-saga/effects";
import type { Action } from "redux-action";

import http from "@http";
import { sendSuccessMessage } from "@modules/app/actionCreator";
import * as actions from "@modules/service/constants/actions";
import actionCreators from "@modules/service/actionCreator";
import type {
  NewManagerSmsNotification,
  ManagerSmsRestriction,
} from "@modules/service/reducer/manager-sms-notifications/types";
import type { HierarchyAssignment } from "@modules/service/types";

type ListResponse<T> = $Exact<{
  content: T,
  paginations: any,
}>;

type ManagerSmsNotificationsListRequest = $Exact<{
  clientId: string,
}>;

export function* onGetSmsRules({
  payload,
}: Action<ManagerSmsNotificationsListRequest>): Generator<
  *,
  void,
  { data: ListResponse<Array<ManagerSmsRestriction>> },
> {
  try {
    if (!payload || !payload.clientId) {
      throw Error("Invalid payload provided");
    }

    const response = yield call(
      http.get,
      `/api/v1/service-admin/client/${payload.clientId}/service-config/absence-management/sms-restriction`,
    );

    yield put(actionCreators.GetManagerSmsNotificationsSmsRulesSuccess(response.data));
  } catch (e) {
    yield put(actionCreators.GetManagerSmsNotificationsSmsRulesError(e.response));
  }
}

type AddManagerSmsNotificationSmsRuleRequest = $Exact<{
  clientId: string,
  rule: NewManagerSmsNotification,
  assignments: Array<HierarchyAssignment>,
}>;

export function* onAddSmsRule({
  payload,
}: Action<AddManagerSmsNotificationSmsRuleRequest>): Generator<*, void, *> {
  try {
    if (!payload || !payload.clientId || !payload.rule || !payload.assignments) {
      throw Error("Invalid payload provided");
    }

    const { data: newRule } = yield call(
      http.post,
      `/api/v1/service-admin/client/${payload.clientId}/service-config/absence-management/sms-restriction`,
      payload.rule,
    );
    if (!newRule) {
      throw Error("Failed to create new rule");
    }

    if (payload.assignments.length) {
      yield all([
        ...payload.assignments.map((a: HierarchyAssignment) => {
          return call(
            http.post,
            `/api/v1/service-admin/client/${payload.clientId}/service-config/absence-management/sms-restriction/${newRule.id}/assignment`,
            a,
          );
        }),
      ]);
    }

    yield all([
      put(actionCreators.AddManagerSmsNotificationSmsRuleSuccess()),
      put(actionCreators.GetManagerSmsNotificationsSmsRules({ clientId: payload.clientId })),
      put(actionCreators.SetShowAddManagerSmsNotificationModal(false)),
      put(sendSuccessMessage("SMS_RESTRICTION_CREATED")),
    ]);
  } catch (e) {
    yield put(actionCreators.AddManagerSmsNotificationSmsRuleError(e.response));
  }
}

type UpdateManagerSmsNotificationSmsRuleRequest = $Exact<{
  clientId: string,
  rule: ManagerSmsRestriction,
  assignments: Array<HierarchyAssignment>,
}>;

export function* onUpdateSmsRule({
  payload,
}: Action<UpdateManagerSmsNotificationSmsRuleRequest>): Generator<*, void, *> {
  try {
    if (!payload || !payload.clientId || !payload.rule || !payload.assignments) {
      throw Error("Invalid payload provided");
    }

    yield call(
      http.put,
      `/api/v1/service-admin/client/${payload.clientId}/service-config/absence-management/sms-restriction/${payload.rule.id}`,
      payload.rule,
    );

    const { data: prevAssignments } = yield call(
      http.get,
      `/api/v1/service-admin/client/${payload.clientId}/service-config/absence-management/sms-restriction/${payload.rule.id}/assignment`,
    );
    if (prevAssignments && prevAssignments.length) {
      yield all([
        ...prevAssignments.map((a: HierarchyAssignment) => {
          return call(
            http.delete,
            `/api/v1/service-admin/client/${
              payload.clientId
            }/service-config/absence-management/sms-restriction/${
              payload.rule.id
            }/assignment/${a.id || ""}`,
          );
        }),
      ]);
    }

    if (payload.assignments.length) {
      yield all([
        ...payload.assignments.map((a: HierarchyAssignment) => {
          return call(
            http.post,
            `/api/v1/service-admin/client/${payload.clientId}/service-config/absence-management/sms-restriction/${payload.rule.id}/assignment`,
            a,
          );
        }),
      ]);
    }

    yield all([
      put(actionCreators.UpdateManagerSmsNotificationSmsRuleSuccess()),
      put(actionCreators.GetManagerSmsNotificationsSmsRules({ clientId: payload.clientId })),
      put(actionCreators.SetShowEditManagerSmsNotificationModal(false)),
      put(sendSuccessMessage("SMS_RESTRICTION_UPDATED")),
    ]);
  } catch (e) {
    yield put(actionCreators.UpdateManagerSmsNotificationSmsRuleError(e.response));
  }
}

type DeleteManagerSmsNotificationSmsRuleRequest = $Exact<{
  clientId: string,
  ruleId: number,
}>;

export function* onDeleteSmsRule({
  payload,
}: Action<DeleteManagerSmsNotificationSmsRuleRequest>): Generator<*, void, void> {
  try {
    if (!payload || !payload.clientId || !payload.ruleId) {
      throw Error("Invalid payload provided");
    }

    yield call(
      http.delete,
      `/api/v1/service-admin/client/${payload.clientId}/service-config/absence-management/sms-restriction/${payload.ruleId}`,
    );

    yield all([
      put(actionCreators.DeleteManagerSmsNotificationSmsRuleSuccess()),
      put(actionCreators.GetManagerSmsNotificationsSmsRules({ clientId: payload.clientId })),
      put(actionCreators.SetShowDeleteManagerSmsNotificationModal(false)),
      put(sendSuccessMessage("SMS_RESTRICTION_DELETED")),
    ]);
  } catch (e) {
    yield put(actionCreators.DeleteManagerSmsNotificationSmsRuleError(e.response));
  }
}

type ManagerSmsNotificationAssignmentsRequest = $Exact<{
  clientId: string,
  ruleId: number,
}>;

export function* onGetSmsAssignments({
  payload,
}: Action<ManagerSmsNotificationAssignmentsRequest>): Generator<
  *,
  void,
  { data: ListResponse<Array<HierarchyAssignment>> },
> {
  try {
    if (!payload || !payload.clientId || !payload.ruleId) {
      throw Error("Invalid payload provided");
    }

    const response = yield call(
      http.get,
      `/api/v1/service-admin/client/${payload.clientId}/service-config/absence-management/sms-restriction/${payload.ruleId}/assignment`,
    );

    yield put(actionCreators.GetManagerSmsNotificationsSmsAssignmentsSuccess(response.data));
  } catch (e) {
    yield put(actionCreators.GetManagerSmsNotificationsSmsAssignmentsError(e.response));
  }
}

// ROOT

export function* ManagerSmsNotificationsSagas(): Generator<*, void, void> {
  yield all([
    takeEvery(actions.GetManagerSmsNotificationsSmsRules, onGetSmsRules),
    takeEvery(actions.AddManagerSmsNotificationSmsRule, onAddSmsRule),
    takeEvery(actions.UpdateManagerSmsNotificationSmsRule, onUpdateSmsRule),
    takeEvery(actions.DeleteManagerSmsNotificationSmsRule, onDeleteSmsRule),
    takeEvery(actions.GetManagerSmsNotificationsSmsAssignments, onGetSmsAssignments),
  ]);
}

export default ManagerSmsNotificationsSagas;
